import { useMemo } from 'react';
import { useLocation, useMatches } from 'react-router-dom';

import { useAppSelector } from '@store/store.tsx';
import {
  selectPlanCapabilities,
  selectSiteApps,
  selectSiteFeatureFlags,
  selectSiteInfoStatus,
} from '@store/slices/settingsSlice.tsx';
import { selectIsStaff, selectIsSuperuser, selectUserGroups, selectUserPermissions, selectUserRoles } from '@store/slices/userSlice.tsx';

import {
  DASHBOARD_NAV_ITEMS,
  findNavItemByPath,
  getVisibleNavItems,
} from '../registry/dashboardNavRegistry.ts';
import type { DashboardNavGroup } from '../registry/types.ts';

export interface DashboardBreadcrumbItem {
  key: string;
  label: string;
  href?: string;
  fromRegistry: boolean;
}

export interface DashboardBreadcrumbsResult {
  items: DashboardBreadcrumbItem[];
  isLoading: boolean;
}

export interface BreadcrumbBaseFallback {
  key: string;
  label: string;
  href: string;
  fromRegistry?: boolean;
}

export interface UseDashboardBreadcrumbsOptions {
  baseNavKey?: string;
  baseFallback?: BreadcrumbBaseFallback;
  groupFilter?: DashboardNavGroup;
}

const HUMANIZE_SEPARATOR = /[-_]+/g;
const GROUP_LABELS: Record<DashboardNavGroup, string> = {
  general: 'General',
  eshop: 'E-shop',
  cms: 'CMS',
  erp: 'ERP',
};

const isDashboardGroup = (value: string): value is DashboardNavGroup => {
  return value === 'general' || value === 'eshop' || value === 'cms' || value === 'erp';
};

const humanizeSegment = (segment: string) => {
  const cleaned = segment.replace(HUMANIZE_SEPARATOR, ' ').trim();
  if (!cleaned) {
    return segment;
  }

  return cleaned
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const useDashboardBreadcrumbs = (
  options?: UseDashboardBreadcrumbsOptions,
): DashboardBreadcrumbsResult => {
  const matches = useMatches();
  const location = useLocation();
  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const siteFeatures = useAppSelector(selectSiteFeatureFlags) ?? {};
  const planCapabilities = useAppSelector(selectPlanCapabilities);
  const siteInfoStatus = useAppSelector(selectSiteInfoStatus);
  const isStaff = useAppSelector(selectIsStaff);
  const isSuperuser = useAppSelector(selectIsSuperuser);
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const groups = useAppSelector(selectUserGroups) ?? [];
  const roles = useAppSelector(selectUserRoles) ?? [];

  const visibleItems = useMemo(
    () =>
      getVisibleNavItems(DASHBOARD_NAV_ITEMS, {
        apps: siteApps,
        features: siteFeatures,
        isStaff,
        isSuperuser,
        permissions,
        groups,
        roles,
        planCapabilities,
      }),
    [groups, isStaff, isSuperuser, permissions, planCapabilities, roles, siteApps, siteFeatures],
  );

  const scopedItems = useMemo(
    () =>
      options?.groupFilter
        ? visibleItems.filter((item) => item.group === options.groupFilter)
        : visibleItems,
    [options?.groupFilter, visibleItems],
  );

  const navItems = scopedItems.length > 0 || !options?.groupFilter ? scopedItems : visibleItems;

  const breadcrumbs = useMemo(() => {
    const baseNavKey = options?.baseNavKey ?? 'dashboard.overview';
    const fallbackBase: BreadcrumbBaseFallback = options?.baseFallback ?? {
      key: 'dashboard:home',
      label: 'Panel de Tienda',
      href: '/dashboard',
      fromRegistry: false,
    };
    const baseNavItem = navItems.find((item) => item.key === baseNavKey) ??
      visibleItems.find((item) => item.key === baseNavKey);
    const baseItem: DashboardBreadcrumbItem = baseNavItem
      ? {
          key: baseNavItem.key,
          label: baseNavItem.title ?? fallbackBase.label,
          href: baseNavItem.to,
          fromRegistry: true,
        }
      : {
          key: fallbackBase.key,
          label: fallbackBase.label,
          href: fallbackBase.href,
          fromRegistry: fallbackBase.fromRegistry ?? false,
        };

    const base: DashboardBreadcrumbItem[] = [baseItem];

    const nonRootMatches = matches.filter((match) => match.pathname !== '/');
    const currentMatch = [...nonRootMatches].reverse().find(Boolean);
    const currentPath = currentMatch ? currentMatch.pathname : undefined;
    const currentHandle = currentMatch as
      | (typeof currentMatch & { handle?: { breadcrumb?: string } })
      | undefined;
    const currentNavItem = currentPath
      ?
          findNavItemByPath(
            navItems,
            currentPath,
            currentPath === location.pathname ? location.search : undefined,
          ) ??
          findNavItemByPath(
            visibleItems,
            currentPath,
            currentPath === location.pathname ? location.search : undefined,
          )
      : undefined;

    if (currentNavItem && currentPath) {
      const groupLabel = GROUP_LABELS[currentNavItem.group] ?? humanizeSegment(currentNavItem.group);
      const segments = currentNavItem.to.split('/').filter(Boolean);
      const parentSegments = segments.slice(0, -1);
      const groupHref = parentSegments.length ? `/${parentSegments.join('/')}` : undefined;
      const lastSegment = currentPath.split('/').filter(Boolean).pop() ?? '';
      const resolvedLabel =
        typeof currentHandle?.handle?.breadcrumb === 'string'
          ? currentHandle.handle.breadcrumb
          : currentNavItem.title ?? humanizeSegment(lastSegment);

      if (currentNavItem.key === baseItem.key && currentPath === currentNavItem.to && !currentHandle?.handle?.breadcrumb) {
        return base;
      }

      const result: DashboardBreadcrumbItem[] = [...base];

      if (groupLabel) {
        result.push({
          key: `group:${currentNavItem.group}`,
          label: groupLabel,
          href: groupHref,
          fromRegistry: false,
        });
      }

      result.push({
        key: currentPath,
        label: resolvedLabel,
        href: currentPath,
        fromRegistry: true,
      });

      return result;
    }

    const seen = new Set<string>();

    nonRootMatches.forEach((match) => {
      const pathname = match.pathname;
      if (!pathname || seen.has(pathname)) {
        return;
      }

      seen.add(pathname);

      const navItem = findNavItemByPath(navItems, pathname) ?? findNavItemByPath(visibleItems, pathname);
      const handle = match as unknown as {
        pathname: string;
        pathnameBase?: string;
        handle?: { breadcrumb?: string };
      };

      const labelFromHandle = handle.handle?.breadcrumb;
      const lastSegment = pathname.split('/').filter(Boolean).pop() ?? '';
      const normalizedSegment = lastSegment.toLowerCase();
      const fallbackGroupLabel = isDashboardGroup(normalizedSegment)
        ? GROUP_LABELS[normalizedSegment]
        : undefined;
      const fallbackLabel = fallbackGroupLabel ?? humanizeSegment(lastSegment);

      base.push({
        key: match.pathname,
        label:
          typeof labelFromHandle === 'string'
            ? labelFromHandle
            : navItem?.title ?? fallbackLabel,
        href: navItem?.to ?? match.pathname,
        fromRegistry: Boolean(navItem),
      });
    });

    return base.filter((item, index, arr) => arr.findIndex((candidate) => candidate.key === item.key) === index);
  }, [location.pathname, location.search, matches, visibleItems]);

  const isLoading = siteInfoStatus === 'loading';

  return { items: breadcrumbs, isLoading };
};

export default useDashboardBreadcrumbs;
